import { currentPostedDate } from '@fruitful/fruitils';
import { subDays } from 'date-fns';
import { router } from 'expo-router';
import { useSetAtom } from 'jotai';
import * as React from 'react';
import { useEffect, useState } from 'react';
import { FlatList } from 'react-native';
import Animated, { FadeIn } from 'react-native-reanimated';
import {
    BodyMediumRegular,
    BodySmallEmphasized,
    BodySmallRegular,
    DateRangeSelectionSheet,
    LabelEmphasized,
    Private,
    Spinner,
    View,
    XStack,
    YStack,
} from 'smoothie';

import { isEmpty } from '../../../../common/misc';
import { isInterestPayoutTransaction } from '../../../../common/utils/bankingUtils';
import {
    DEFAULT_DATE_FORMAT,
    format,
    SHORT_MONTH_DATE_FORMAT_SLASH,
    tomorrow,
} from '../../../../common/utils/dateUtils';
import { captureException } from '../../../../logger/Logger';
import { getTransactions } from '../../../api/TransactionsApi';
import { selectedTransactionAtom } from '../../../state/SpacesAtoms';
import type { BankAccountDisplayDetail } from '../../../types/BankAccount';
import {
    AMOUNT_SIGN_BY_TYPE,
    type LegacyTransaction,
    LegacyTransactionStatus,
} from '../../../types/Transactions';
import LegacyTransactionIcon from './LegacyTransactionIcon';

const TRANSACTIONS_PER_PAGE = 20;

interface SavingsSpaceTransactionsProps {
    account: BankAccountDisplayDetail;
}

const LegacyTransactions = ({ account }: SavingsSpaceTransactionsProps) => {
    const [isInitialLoad, setIsInitialLoad] = useState(true);
    const [isLoadingMore, setIsLoadingMore] = useState(false);

    const [transactions, setTransactions] = useState<LegacyTransaction[]>([]);
    const [transactionsPageToken, setTransactionsPageToken] = useState('');
    const [nextTransactionsPageToken, setNextTransactionsPageToken] =
        useState('');

    const [isDateSelectionSheetOpen, setIsDateSelectionSheetOpen] =
        useState(false);
    const [toDate, setToDate] = useState(currentPostedDate());
    const [fromDate, setFromDate] = useState(subDays(toDate, 120)); // Default from date to 120 days of transactions for savings account

    const setSelectedTransaction = useSetAtom(selectedTransactionAtom);

    const getQueryData = () => ({
        limit: TRANSACTIONS_PER_PAGE,
        from_date: format(fromDate, DEFAULT_DATE_FORMAT),
        to_date: format(toDate, DEFAULT_DATE_FORMAT),
        ...(!isEmpty(transactionsPageToken)
            ? { page_token: transactionsPageToken }
            : {}),
    });

    // Using a regular API instead of a hook to be able to trigger refetches more explicitly.
    const fetchTransactions = async () => {
        try {
            const { result, next_page_token } = await getTransactions({
                bankAccountId: account.bankAccountId,
                query: getQueryData(),
            });
            setTransactions(
                isLoadingMore ? transactions.concat(result) : result,
            );
            setNextTransactionsPageToken(next_page_token);
        } catch (e) {
            captureException(e);
        } finally {
            setIsInitialLoad(false);
            setIsLoadingMore(false);
        }
    };

    useEffect(() => {
        // Empty transactions table when selected account changes
        setIsInitialLoad(true);
        setTransactions([]);
        setNextTransactionsPageToken('');
        setTransactionsPageToken('');

        // Fetch transactions for selected account
        void fetchTransactions();
        // Important that this dependency is a primitive value because the "has this changed" comparison is shallow,
        // which means this effect would always be triggered even if the account has not actually changed
    }, [account.syncteraAccountId]);

    useEffect(() => {
        // Fetch next page of transactions when page token is set (enabling infinite scroll), or when date range changes.
        void fetchTransactions();
    }, [transactionsPageToken, toDate, fromDate]);

    // TODO: Add error view

    return (
        <YStack flex={1}>
            <XStack
                alignItems="center"
                justifyContent="space-between"
                marginBottom={8}
            >
                <BodyMediumRegular>Transactions</BodyMediumRegular>
                <View
                    backgroundColor="$gray100"
                    borderRadius={100}
                    paddingHorizontal={16}
                    paddingVertical={12}
                    onPress={() => {
                        setIsDateSelectionSheetOpen(true);
                    }}
                    cursor="pointer"
                >
                    <LabelEmphasized>Filter by date</LabelEmphasized>
                </View>
            </XStack>
            {isInitialLoad && <Spinner />}
            {!isInitialLoad && !isLoadingMore && isEmpty(transactions) && (
                <Animated.View entering={FadeIn.duration(700)}>
                    <BodySmallRegular marginTop={12}>
                        No transactions for dates selected.
                    </BodySmallRegular>
                </Animated.View>
            )}
            {!isInitialLoad && transactions?.[0] != null && (
                <View flex={1}>
                    <FlatList
                        data={transactions ?? []}
                        onEndReached={() => {
                            if (
                                !isLoadingMore &&
                                nextTransactionsPageToken != null &&
                                nextTransactionsPageToken !==
                                    transactionsPageToken
                            ) {
                                setIsLoadingMore(true);
                                setTransactionsPageToken(
                                    nextTransactionsPageToken,
                                );
                                setNextTransactionsPageToken('');
                            }
                        }}
                        ListFooterComponent={
                            isLoadingMore ? (
                                <Spinner marginVertical={16} />
                            ) : (
                                <></>
                            )
                        }
                        renderItem={({ item: transaction }) => (
                            <TransactionRow
                                transaction={transaction}
                                onPress={() => {
                                    setSelectedTransaction(transaction);
                                    router.navigate(
                                        '/banking/transaction-details',
                                    );
                                }}
                            />
                        )}
                    />
                </View>
            )}
            <DateRangeSelectionSheet
                maximumDate={tomorrow()}
                defaultToDate={toDate}
                defaultFromDate={fromDate}
                isOpen={isDateSelectionSheetOpen}
                onClose={() => {
                    setIsDateSelectionSheetOpen(false);
                }}
                onConfirm={(fromDate: Date, toDate: Date) => {
                    setFromDate(fromDate);
                    setToDate(toDate);
                    setIsDateSelectionSheetOpen(false);
                }}
            />
        </YStack>
    );
};

interface TransactionRowProps {
    transaction: LegacyTransaction;
    onPress: () => void;
}

const TransactionRow = ({ transaction, onPress }: TransactionRowProps) => {
    const { dcSign, description, created, formattedAmount, status } =
        transaction;

    const isPending = status === LegacyTransactionStatus.PENDING;

    return (
        <YStack
            paddingVertical={12}
            borderBottomWidth={1}
            borderBottomColor="$gray200"
            justifyContent="space-between"
            onPress={onPress}
            cursor="pointer"
            hoverStyle={{ backgroundColor: '$gray100' }}
        >
            <XStack justifyContent="space-between" alignItems="center">
                <XStack gap={8} maxWidth="60%" alignItems="flex-start">
                    <LegacyTransactionIcon
                        isInterestPayout={isInterestPayoutTransaction(
                            transaction,
                        )}
                        dcSign={dcSign}
                    />
                    <YStack marginTop={4}>
                        <BodyMediumRegular>{description}</BodyMediumRegular>
                        <XStack marginTop={2}>
                            <LabelEmphasized color="$gray900">
                                {format(created, SHORT_MONTH_DATE_FORMAT_SLASH)}
                                {'  '}·{'  '}
                            </LabelEmphasized>
                            <LabelEmphasized
                                color={isPending ? '$orange1100' : '$green1000'}
                            >
                                {isPending ? 'Pending' : 'Complete'}
                            </LabelEmphasized>
                        </XStack>
                    </YStack>
                </XStack>
                <View paddingVertical={4} paddingHorizontal={8}>
                    <Private>
                        <BodySmallEmphasized>
                            {AMOUNT_SIGN_BY_TYPE[dcSign]}
                            {formattedAmount}
                        </BodySmallEmphasized>
                    </Private>
                </View>
            </XStack>
        </YStack>
    );
};

export default LegacyTransactions;
